import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../auth/useAuth";
import {
  getPlanLabel,
  normalizePlanCode,
} from "../components/billing/plans";

function safeApiError(err, fallback = "Something went wrong.") {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

function shorten(value) {
  if (!value || typeof value !== "string") return "-";
  if (value.length <= 14) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

function Badge({ children, tone = "slate" }) {
  const toneMap = {
    slate:
      "border-slate-200 bg-white text-slate-700 shadow-sm",
    blue:
      "border-blue-200 bg-blue-50 text-blue-700 shadow-sm",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm",
    amber:
      "border-[#E6C978]/50 bg-[#FFF8E1] text-[#8A6A1F] shadow-sm",
    rose:
      "border-rose-200 bg-rose-50 text-rose-700 shadow-sm",
    violet:
      "border-violet-200 bg-violet-50 text-violet-700 shadow-sm",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide ${toneMap[tone] || toneMap.slate}`}
    >
      {children}
    </span>
  );
}
function planTone(planCode) {
  const plan = normalizePlanCode(planCode);

  if (plan === "starter") return "amber";
  if (plan === "pro") return "blue";
  if (plan === "team") return "violet";

  return "slate";
}

function supportStatusTone(status) {
  const s = String(status || "").trim().toUpperCase();

  if (s === "OPEN") return "amber";
  if (s === "IN_REVIEW") return "blue";
  if (s === "WAITING_FOR_USER") return "violet";
  if (s === "RESOLVED") return "emerald";
  if (s === "CLOSED") return "slate";

  return "slate";
}

function supportPriorityTone(priority) {
  const p = String(priority || "").trim().toUpperCase();

  if (p === "HIGH" || p === "URGENT") return "rose";
  if (p === "MEDIUM") return "amber";
  if (p === "LOW") return "slate";

  return "slate";
}

function humanizeSupportIssueType(issueType) {
  switch (String(issueType || "").trim().toUpperCase()) {
    case "DEPLOYMENT_ERROR":
      return "Problem";
    case "FEATURE_REQUEST":
      return "Feature";
    case "PAYMENT_ISSUE":
      return "Billing";
    case "NETWORK_ERROR":
      return "Network";
    case "CONTRACT_VERIFICATION":
      return "Verification";
    case "BUG_REPORT":
      return "Bug";
    default:
      return "General";
  }
}

function StatCard({ label, value, subtext, tone = "neutral" }) {
  const toneClass =
    tone === "brand"
      ? "border-[#E6C978]/40 bg-gradient-to-br from-[#FFF8E1] via-white to-slate-50"
      : tone === "success"
        ? "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-slate-50"
        : tone === "warning"
          ? "border-[#E6C978]/40 bg-gradient-to-br from-[#FFF8E1] via-white to-slate-50"
          : "border-slate-200 bg-gradient-to-br from-white via-white to-slate-50";

  return (
    <div className={`rounded-[1.5rem] border p-5 shadow-[0_14px_40px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:shadow-[0_18px_50px_rgba(15,23,42,0.09)] ${toneClass}`}>
      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
        {label}
      </div>
      <div className="mt-3 text-3xl font-semibold tracking-[-0.04em] text-slate-950">
        {value}
      </div>
      {subtext ? (
        <div className="mt-2 text-xs leading-5 text-slate-500">{subtext}</div>
      ) : null}
    </div>
  );
}

function SectionCard({ title, description, actions, children }) {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
      <div className="flex flex-col gap-4 border-b border-slate-200 bg-gradient-to-r from-white via-slate-50/70 to-[#FFF8E1]/40 px-6 py-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-[-0.02em] text-slate-950">
            {title}
          </h2>
          {description ? (
            <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">
              {description}
            </p>
          ) : null}
        </div>
        {actions ? <div className="shrink-0">{actions}</div> : null}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function InlineNotice({ tone = "neutral", children }) {
  const toneClass =
    tone === "error"
      ? "border-rose-200 bg-gradient-to-r from-rose-50 to-red-50 text-rose-800"
      : tone === "warning"
        ? "border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-800"
        : "border-slate-200 bg-gradient-to-r from-slate-50 to-white text-slate-700";

  return (
    <div className={`rounded-2xl border p-4 text-sm ${toneClass}`}>{children}</div>
  );
}

export default function AdminDashboard() {
  const { isAuthed, restoreLoading, authReady = true } = useAuth();

  const [accountUsage, setAccountUsage] = useState(null);
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessError, setAccessError] = useState("");

  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportSummary, setSupportSummary] = useState(null);

  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState("");

  const [search, setSearch] = useState("");
  const [savingUserId, setSavingUserId] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  const loadAccess = useCallback(async () => {
    if (!authReady || restoreLoading) {
      return;
    }

    if (!isAuthed) {
      setAccountUsage(null);
      setAccessError("");
      setAccessLoading(false);
      return;
    }

    setAccessLoading(true);
    setAccessError("");

    try {
      const res = await api.get("/account/usage", {
        params: { _ts: Date.now() },
        headers: {
          "Cache-Control": "no-store, no-cache, max-age=0",
          Pragma: "no-cache",
        },
      });
      setAccountUsage(res.data || null);
    } catch (err) {
      setAccountUsage(null);
      setAccessError(safeApiError(err, "Failed to verify admin access."));
    } finally {
      setAccessLoading(false);
    }
  }, [isAuthed, restoreLoading, authReady]);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError("");

    try {
      const ts = Date.now();

      const [overviewRes, usersRes, deploymentsRes, supportRes] = await Promise.all([
        api.get("/admin/overview", {
          params: { _ts: ts },
          headers: {
            "Cache-Control": "no-store, no-cache, max-age=0",
            Pragma: "no-cache",
          },
        }),
        api.get("/admin/users", {
          params: {
            ...(search.trim() ? { search: search.trim() } : {}),
            _ts: ts,
          },
          headers: {
            "Cache-Control": "no-store, no-cache, max-age=0",
            Pragma: "no-cache",
          },
        }),
        api.get("/admin/deployments", {
          params: { limit: 50, _ts: ts },
          headers: {
            "Cache-Control": "no-store, no-cache, max-age=0",
            Pragma: "no-cache",
          },
        }),
        api.get("/api/support/admin/tickets", {
          params: { limit: 20, _ts: ts },
          headers: {
            "Cache-Control": "no-store, no-cache, max-age=0",
            Pragma: "no-cache",
          },
        }),
      ]);

      setOverview(overviewRes.data?.summary || null);
      setUsers(Array.isArray(usersRes.data?.users) ? usersRes.data.users : []);
      setDeployments(
        Array.isArray(deploymentsRes.data?.deployments)
          ? deploymentsRes.data.deployments
          : [],
      );

      const supportData = supportRes.data?.data || {};
      const supportItems = Array.isArray(supportData?.items) ? supportData.items : [];

      setSupportTickets(supportItems);

      setSupportSummary({
        open: supportItems.filter((ticket) => ticket.status === "OPEN").length,
        inReview: supportItems.filter((ticket) => ticket.status === "IN_REVIEW").length,
        waitingForUser: supportItems.filter(
          (ticket) => ticket.status === "WAITING_FOR_USER",
        ).length,
        resolved: supportItems.filter((ticket) => ticket.status === "RESOLVED").length,
      });
    } catch (err) {
      setDashboardError(safeApiError(err, "Failed to load admin dashboard."));
    } finally {
      setDashboardLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  useEffect(() => {
    if (!authReady || restoreLoading) return;
    if (!isAuthed) return;

    const normalizedRole = String(accountUsage?.role || "")
      .trim()
      .toLowerCase();

    const canAccess =
      Boolean(
        accountUsage?.isPrivileged ||
        accountUsage?.isInternal ||
        accountUsage?.bypassLimits ||
        accountUsage?.isAdmin
      ) && ["owner", "admin"].includes(normalizedRole);

    if (!canAccess) return;

    loadDashboard();
  }, [authReady, restoreLoading, isAuthed, accountUsage, loadDashboard, refreshTick]);

  const isPrivilegedUser = Boolean(accountUsage?.isPrivileged);
  const currentRole = String(accountUsage?.role || "user").toLowerCase();

  const canAccessAdmin =
    isPrivilegedUser && (currentRole === "owner" || currentRole === "admin");

  const filteredUsers = useMemo(() => users, [users]);

  const refreshAll = useCallback(() => {
    setRefreshTick((v) => v + 1);
  }, []);

  const updateUserAccess = useCallback(
    async (userId, patch) => {
      setSavingUserId(userId);
      try {
        await api.patch(`/admin/users/${userId}/access`, patch);
        refreshAll();
      } catch (err) {
        alert(safeApiError(err, "Failed to update user access."));
      } finally {
        setSavingUserId("");
      }
    },
    [refreshAll],
  );

  const updateUserPlan = useCallback(
    async (userId, patch) => {
      setSavingUserId(userId);
      try {
        await api.patch(`/admin/users/${userId}/plan`, patch);
        refreshAll();
      } catch (err) {
        alert(safeApiError(err, "Failed to update user plan."));
      } finally {
        setSavingUserId("");
      }
    },
    [refreshAll],
  );

  if (!authReady || restoreLoading || accessLoading) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "14px"
      }}>
        Checking admin access...
      </div>
    );
  }

  if (!isAuthed || !canAccessAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] text-slate-950">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.14),_transparent_32%),radial-gradient(circle_at_top_right,_rgba(15,23,42,0.07),_transparent_28%),linear-gradient(to_bottom,_#F7F8FA,_#F1F3F6_46%,_#F7F8FA)]" />

      <div className="mx-auto max-w-7xl space-y-7 px-4 py-8 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2rem] border border-slate-800 bg-[#050713] p-6 text-white shadow-[0_24px_80px_rgba(15,23,42,0.24)] md:flex md:items-center md:justify-between lg:p-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.28),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.10),_transparent_32%)]" />
          <div className="relative">
            <div className="inline-flex items-center rounded-full border border-[#C59B45]/30 bg-[#C59B45]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#E6C978]">
              Krionex Admin Console
            </div>
            <h1 className="mt-5 max-w-4xl text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
              Control users, plans, deployments, and support operations
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300">
              Internal operations dashboard for workspace access control, manual upgrades,
              deployment visibility, support queue oversight, and admin-level testing.
            </p>
          </div>

          <div className="relative mt-5 flex flex-wrap gap-3 md:mt-0">
            <button
              onClick={refreshAll}
              className="rounded-2xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/15"
            >
              Refresh
            </button>

            <Link
              to="/"
              className="rounded-2xl border border-[#E6C978]/40 bg-white px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-[#FFF8E1]"
            >
              Back to Studio
            </Link>
          </div>
        </div>

        {accessError ? (
          <InlineNotice tone="error">{accessError}</InlineNotice>
        ) : null}

        {dashboardError ? (
          <InlineNotice tone="warning">{dashboardError}</InlineNotice>
        ) : null}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Total Users"
            value={overview?.totalUsers ?? "—"}
            subtext="Registered wallets"
            tone="brand"
          />
          <StatCard
            label="Total Deployments"
            value={overview?.totalDeployments ?? "—"}
            subtext="All confirmed deployments"
          />
          <StatCard
            label="Paid Users"
            value={
              overview
                ? Number(overview?.starterUsers || 0) + Number(overview?.proUsers || 0)
                : "—"
            }
            subtext="Starter and Pro users"
            tone="success"
          />
          <StatCard
            label="Deployments This Month"
            value={overview?.deploymentsThisMonth ?? "—"}
            subtext={overview?.monthKey ? `Usage period ${overview.monthKey}` : ""}
            tone="warning"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Open Tickets"
            value={supportSummary?.open || 0}
            subtext="Support requests awaiting action"
            tone="warning"
          />
          <StatCard
            label="In Review"
            value={supportSummary?.inReview || 0}
            subtext="Tickets currently being worked"
            tone="brand"
          />
          <StatCard
            label="Waiting for User"
            value={supportSummary?.waitingForUser || 0}
            subtext="Needs user response"
          />
          <StatCard
            label="Resolved"
            value={supportSummary?.resolved || 0}
            subtext="Closed successfully"
            tone="success"
          />
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <SectionCard
            title="Users"
            description="Manage user/admin roles, bypass flags, internal test accounts, and manual plan overrides."
            actions={
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search wallet address"
                className="w-64 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#C59B45]/10"
              />
            }
          >
            {dashboardLoading ? (
              <div className="text-sm text-slate-500">Loading users…</div>
            ) : filteredUsers.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
                No users matched the current search.
              </div>
            ) : (
              <div className="space-y-4">
                {filteredUsers.map((user) => {
                  const isSaving = savingUserId === user.id;

                  return (
                    <div
                      key={user.id}
                      className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-[0_12px_35px_rgba(15,23,42,0.05)] transition hover:border-[#E6C978]/50 hover:shadow-[0_16px_45px_rgba(15,23,42,0.08)]"
                    >
                      <div className="grid gap-5 xl:grid-cols-[1.3fr_0.75fr_0.85fr_0.9fr_0.9fr]">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Wallet
                          </div>
                          <div className="mt-2 text-lg font-semibold text-slate-900">
                            {shorten(user.walletAddress)}
                          </div>
                          <div className="mt-1 break-all text-sm text-slate-500">
                            {user.walletAddress}
                          </div>
                          <div className="mt-2 text-xs text-slate-400">
                            Joined {formatDateTime(user.createdAt)}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Access
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Badge
                              tone={
                                user.role === "owner"
                                  ? "violet"
                                  : user.role === "admin"
                                    ? "blue"
                                    : "slate"
                              }
                            >
                              {user.role}
                            </Badge>
                          </div>
                        </div>

                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Plan
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Badge tone={planTone(user.plan?.planCode)}>
                              {getPlanLabel(user.plan?.planCode || "free")}
                            </Badge>
                            <Badge tone="amber">
                              {user.plan?.status || "inactive"}
                            </Badge>
                          </div>
                        </div>

                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Usage
                          </div>
                          <div className="mt-2 text-sm text-slate-700">
                            <div>Deployments: {user.usage?.deploymentsUsed ?? 0}</div>
                            <div className="mt-1 text-slate-500">
                              Total records: {user.counts?.deployments ?? 0}
                            </div>
                            <div className="mt-1 text-slate-500">
                              Tickets: {user.counts?.supportTickets ?? 0}
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Flags
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {user.bypassLimits ? <Badge tone="emerald">bypass</Badge> : null}
                            {user.isInternal ? <Badge tone="amber">internal</Badge> : null}
                            {user.adminNotes ? <Badge tone="slate">note</Badge> : null}
                          </div>

                          {user.adminNotes ? (
                            <div className="mt-2 text-xs leading-5 text-slate-500">
                              {user.adminNotes}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="mt-5 grid gap-4 lg:grid-cols-2">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 shadow-inner">
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Access Controls
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button
                              disabled={isSaving}
                              onClick={() =>
                                updateUserAccess(user.id, {
                                  role: "user",
                                  bypassLimits: false,
                                  isInternal: false,
                                })
                              }
                              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-medium transition hover:bg-gray-50 disabled:opacity-60"
                            >
                              Set User
                            </button>

                            <button
                              disabled={isSaving}
                              onClick={() =>
                                updateUserAccess(user.id, {
                                  role: "admin",
                                  bypassLimits: false,
                                  isInternal: true,
                                })
                              }
                              className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 transition hover:bg-blue-100 disabled:opacity-60"
                            >
                              Set Admin
                            </button>
                            <div className="mt-3 text-xs text-slate-500">
                              Owner access is managed only through secure server configuration and is not editable from the admin dashboard.
                            </div>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 shadow-inner">
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Plan Controls
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button
                              disabled={isSaving}
                              onClick={() =>
                                updateUserPlan(user.id, {
                                  planCode: "free",
                                  status: "active",
                                  manualOverride: true,
                                })
                              }
                              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-medium transition hover:bg-gray-50 disabled:opacity-60"
                            >
                              Free
                            </button>

                            <button
                              disabled={isSaving}
                              onClick={() =>
                                updateUserPlan(user.id, {
                                  planCode: "starter",
                                  status: "active",
                                  manualOverride: true,
                                })
                              }
                              className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 transition hover:bg-amber-100 disabled:opacity-60"
                            >
                              Starter
                            </button>

                            <button
                              disabled={isSaving}
                              onClick={() =>
                                updateUserPlan(user.id, {
                                  planCode: "pro",
                                  status: "active",
                                  manualOverride: true,
                                })
                              }
                              className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 transition hover:bg-blue-100 disabled:opacity-60"
                            >
                              Pro
                            </button>

                            <button
                              disabled={isSaving}
                              onClick={() =>
                                updateUserPlan(user.id, {
                                  planCode: "team",
                                  status: "active",
                                  manualOverride: true,
                                })
                              }
                              className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-medium text-violet-700 transition hover:bg-violet-100 disabled:opacity-60"
                            >
                              Team
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Plan Mix"
            description="Quick operational snapshot of current subscription distribution."
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <span className="text-sm text-slate-600">Free</span>
                <span className="text-lg font-semibold text-slate-900">
                  {overview?.freeUsers ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-[#E6C978]/50 bg-[#FFF8E1] px-4 py-3 shadow-sm">
                <span className="text-sm text-amber-700">Starter</span>
                <span className="text-lg font-semibold text-amber-900">
                  {overview?.starterUsers ?? 0}
                </span>
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 shadow-sm">
                <span className="text-sm font-medium text-blue-700">Pro</span>
                <span className="text-lg font-semibold text-blue-900">
                  {overview?.proUsers ?? 0}
                </span>
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3">
                <span className="text-sm text-violet-700">Team</span>
                <span className="text-lg font-semibold text-violet-900">
                  {overview?.teamUsers ?? 0}
                </span>
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
                <span className="text-sm text-slate-600">Admins</span>
                <span className="text-lg font-semibold text-slate-900">
                  {(overview?.adminUsers ?? 0) + (overview?.ownerUsers ?? 0)}
                </span>
              </div>
            </div>
          </SectionCard>
        </div>

        <SectionCard
          title="Recent Deployments"
          description="Latest confirmed deployments across users and networks."
        >
          {dashboardLoading ? (
            <div className="text-sm text-slate-500">Loading deployments…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                    <th className="px-3 py-3 font-medium">Token</th>
                    <th className="px-3 py-3 font-medium">Type</th>
                    <th className="px-3 py-3 font-medium">Network</th>
                    <th className="px-3 py-3 font-medium">Contract</th>
                    <th className="px-3 py-3 font-medium">User</th>
                    <th className="px-3 py-3 font-medium">Verification</th>
                    <th className="px-3 py-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {deployments.map((deployment) => (
                    <tr
                      key={deployment.id}
                      className="border-b border-slate-100 transition hover:bg-[#FFF8E1]/35"
                    >
                      <td className="px-3 py-4">
                        <div className="font-medium text-slate-900">
                          {deployment.tokenName || "-"}
                        </div>
                        <div className="text-xs text-slate-500">
                          {deployment.tokenSymbol || "-"}
                        </div>
                      </td>

                      <td className="px-3 py-4">
                        <Badge tone="slate">{deployment.tokenType || "-"}</Badge>
                      </td>

                      <td className="px-3 py-4 text-slate-700">
                        {deployment.network || "-"}
                      </td>

                      <td className="px-3 py-4">
                        {deployment.explorerUrl ? (
                          <a
                            href={deployment.explorerUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="font-medium text-blue-600 hover:underline"
                          >
                            {shorten(deployment.contractAddress)}
                          </a>
                        ) : (
                          <span>{shorten(deployment.contractAddress)}</span>
                        )}
                      </td>

                      <td className="px-3 py-4">
                        <div className="text-slate-900">
                          {shorten(deployment.user?.walletAddress)}
                        </div>
                        <div className="mt-1">
                          <Badge
                            tone={
                              deployment.user?.role === "owner"
                                ? "violet"
                                : deployment.user?.role === "admin"
                                  ? "blue"
                                  : "slate"
                            }
                          >
                            {deployment.user?.role || "user"}
                          </Badge>
                        </div>
                      </td>

                      <td className="px-3 py-4">
                        <Badge
                          tone={
                            deployment.verificationStatus === "verified"
                              ? "emerald"
                              : deployment.verificationStatus === "failed"
                                ? "rose"
                                : "amber"
                          }
                        >
                          {deployment.verificationStatus || "not_requested"}
                        </Badge>
                      </td>

                      <td className="px-3 py-4 text-slate-500">
                        {formatDateTime(deployment.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="Support Queue"
          description="Latest complaints, questions, billing issues, and feature requests submitted through Help Center."
        >
          {dashboardLoading ? (
            <div className="text-sm text-slate-500">Loading support tickets…</div>
          ) : supportTickets.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
              No support tickets found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                    <th className="px-3 py-3 font-medium">Ticket</th>
                    <th className="px-3 py-3 font-medium">Issue</th>
                    <th className="px-3 py-3 font-medium">Priority</th>
                    <th className="px-3 py-3 font-medium">Status</th>
                    <th className="px-3 py-3 font-medium">Wallet</th>
                    <th className="px-3 py-3 font-medium">Network</th>
                    <th className="px-3 py-3 font-medium">Created</th>
                    <th className="px-3 py-3 font-medium">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {supportTickets.map((ticket) => (
                    <tr
                      key={ticket.id || ticket.publicId}
                      className="border-b border-slate-100 transition hover:bg-[#FFF8E1]/35"
                    >
                      <td className="px-3 py-4">
                        <div className="font-medium text-slate-900">
                          {ticket.subject || "-"}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {ticket.publicId || "-"}
                        </div>
                      </td>

                      <td className="px-3 py-4">
                        <Badge tone="slate">
                          {humanizeSupportIssueType(ticket.issueType)}
                        </Badge>
                      </td>

                      <td className="px-3 py-4">
                        <Badge tone={supportPriorityTone(ticket.priority)}>
                          {String(ticket.priority || "LOW").toLowerCase()}
                        </Badge>
                      </td>

                      <td className="px-3 py-4">
                        <Badge tone={supportStatusTone(ticket.status)}>
                          {String(ticket.status || "OPEN").toLowerCase()}
                        </Badge>
                      </td>

                      <td className="px-3 py-4">
                        <div className="text-slate-900">
                          {shorten(ticket.walletAddress)}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {ticket.walletAddress || "-"}
                        </div>
                      </td>

                      <td className="px-3 py-4 text-slate-700">
                        {ticket.network || "-"}
                      </td>

                      <td className="px-3 py-4 text-slate-500">
                        {formatDateTime(ticket.createdAt)}
                      </td>

                      <td className="px-3 py-4">
                        <Link
                          to={`/help-center/${ticket.publicId}?admin=1&wallet=${encodeURIComponent(ticket.walletAddress)}`}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}